// src/App.jsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext.jsx'
import { PlanProvider } from './context/PlanContext.jsx';
import AuthPage from './pages/AuthPage.jsx';
import RoleSelectionPage from './pages/RoleSelectionPage.jsx';
import CustomerDashboard from './pages/CustomerDashboard.jsx';
import BusinessDashboard from './pages/BusinessDashboard.jsx';
import AuthCallback from './pages/AuthCallback.jsx';
import OAuthConsent from './pages/OAuthConsent.jsx';
import CalendarCallback from './pages/CalendarCallback.jsx';
import GoogleCalendarCallback from './pages/GoogleCalendarCallback.jsx';
import HubSpotCallback from './pages/HubSpotCallback.jsx';
import VennBooking from './pages/VennBooking.jsx';
import BookingSuccess from './pages/BookingSuccess.jsx';
import BookingCancelled from './pages/BookingCancelled.jsx';
import PublicEventPage from './pages/events/PublicEventPage.jsx';
import EventSuccess from './pages/events/EventSuccess.jsx';
import EventsList from './pages/events/EventsList.jsx';
import EventForm from './pages/events/EventForm.jsx';
import EventAttendees from './pages/events/EventAttendees.jsx';
import InvitationAccept from './pages/InvitationAccept.jsx';
import ProposalActionPage from './pages/ProposalActionPage.jsx';
import ProposalDetailPage from './pages/ProposalDetailPage.jsx';
import IntegrationMarketplace from './pages/IntegrationMarketplace.jsx';
import Privacy from './pages/Privacy.jsx';
import Terms from './pages/Terms.jsx';
import ApiKeys from './pages/ApiKeys.jsx';
import Developers from './pages/Developers.jsx';
import Waitlist from './pages/Waitlist.jsx';
import GetStarted from './pages/GetStarted.jsx';
import SchedulingAudit from './pages/SchedulingAudit.jsx';
import VennioVibeCoders from './pages/vennio-vibe-coders.jsx';
import LandingPage from './pages/LandingPage.jsx';
import Pricing from './pages/Pricing.jsx';
import BillingSettings from './pages/BillingSettings.jsx';
import SetupRequired from './components/SetupRequired.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { supabase } from './lib/supabase.js';
import styles from './App.module.css';

function App() {
  // Check if Supabase is configured
  const isConfigured = !!supabase;

  // Show setup screen if not configured
  if (!isConfigured) {
    return <SetupRequired />;
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <PlanProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              // Default options for all toasts
              duration: 4000,
              style: {
                background: '#1a1612',
                color: '#faf7f2',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#4a7c59',
                  secondary: '#faf7f2',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#c0392b',
                  secondary: '#faf7f2',
                },
              },
            }}
          />
          <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/signup" element={<Navigate to="/auth" replace />} />

          {/* OAuth 2.1 consent for remote MCP connectors (claude.ai).
              Public route — the page redirects to /auth itself when logged out
              so the full authorize query string survives the round-trip. */}
          <Route path="/oauth/consent" element={<OAuthConsent />} />

          {/* Public Venn booking page */}
          <Route path="/venn/:shortCode" element={<VennBooking />} />
          <Route path="/v/:shortCode" element={<VennBooking />} />
          <Route path="/booking/success" element={<BookingSuccess />} />
          <Route path="/booking/cancelled" element={<BookingCancelled />} />

          {/* Public e-Venn-t pages (must NOT be inside ProtectedRoute) */}
          <Route path="/e/:shortCode" element={<PublicEventPage />} />
          <Route path="/e/:shortCode/success" element={<EventSuccess />} />

          {/* Public invitation acceptance page */}
          <Route path="/invite/:token" element={<InvitationAccept />} />

          {/* Wave 3: public magic-link proposal landing — UNAUTHENTICATED */}
          <Route path="/p/:token" element={<ProposalActionPage />} />

          {/* Public Integration Marketplace */}
          <Route path="/integrations" element={<IntegrationMarketplace />} />

          {/* Public Developer Portal */}
          <Route path="/developers" element={<Developers />} />

          {/* Public Waitlist Page */}
          <Route path="/waitlist" element={<Waitlist />} />

          {/* Scheduling Health Check Audit */}
          <Route path="/audit" element={<SchedulingAudit />} />

          {/* Vibe Coder Onboarding */}
          <Route path="/get-started" element={<GetStarted />} />

          {/* Vibe Coders Marketing Page */}
          <Route path="/vibe-coders" element={<VennioVibeCoders />} />

          {/* Legal Pages */}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/* API Keys Management */}
          <Route
            path="/api-keys"
            element={
              <ProtectedRoute>
                <ApiKeys />
              </ProtectedRoute>
            }
          />

          {/* Billing Settings */}
          <Route
            path="/settings/billing"
            element={
              <ProtectedRoute>
                <BillingSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <BillingSettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/role-selection"
            element={
              <ProtectedRoute>
                <RoleSelectionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar/callback"
            element={
              <ProtectedRoute>
                <CalendarCallback />
              </ProtectedRoute>
            }
          />
          {/* Google OAuth callback - public route since user may not be logged in yet */}
          <Route path="/calendar/callback/google" element={<GoogleCalendarCallback />} />
          <Route
            path="/integrations/hubspot/callback"
            element={
              <ProtectedRoute>
                <HubSpotCallback />
              </ProtectedRoute>
            }
          />

          {/* Wave 4: auth-gated single proposal view */}
          <Route
            path="/proposals/:id"
            element={
              <ProtectedRoute>
                <ProposalDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            }
          />

          {/* Events organiser pages */}
          <Route
            path="/events"
            element={<ProtectedRoute><EventsList /></ProtectedRoute>}
          />
          <Route
            path="/events/new"
            element={<ProtectedRoute><EventForm /></ProtectedRoute>}
          />
          <Route
            path="/events/:id/edit"
            element={<ProtectedRoute><EventForm /></ProtectedRoute>}
          />
          <Route
            path="/events/:id/attendees"
            element={<ProtectedRoute><EventAttendees /></ProtectedRoute>}
          />

          <Route path="/pricing" element={<Pricing />} />
          <Route path="/" element={<LandingPage />} />
        </Routes>
          </PlanProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className={styles.loadingPage}>
        <div className={styles.loadingCenter}>
          <div className={styles.spinner} />
          <p className={styles.loadingText}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Preserve the intended destination AND its query string, so a deep link
    // with attribution params (e.g. docs → /api-keys?utm_*&ref=) carries those
    // onto /auth for capture instead of being dropped at the bounce.
    return (
      <Navigate
        to={`/auth${location.search}`}
        state={{ returnUrl: location.pathname + location.search }}
        replace
      />
    );
  }

  return children;
}

// Route to correct dashboard based on role
function DashboardRouter() {
  const { profile, loading, profileLoading, user, profileError, retryFetchProfile } = useAuth();

  if (import.meta.env.VITE_DEBUG) {
    console.log('=== DashboardRouter rendered ===');
    console.log('Loading:', loading);
    console.log('Profile Loading:', profileLoading);
    console.log('Profile:', profile);
  }

  // Show loading if either auth or profile is loading
  if (loading || profileLoading) {
    return (
      <div className={styles.loadingPage}>
        <div className={styles.loadingCenter}>
          <div className={styles.spinner} />
          <p className={styles.loadingText}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // If there's a profile error (timeout/network), show error screen with retry
  if (user && profileError && !profile) {
    return (
      <div className={styles.errorPage}>
        <div className={styles.errorCard}>
          <div className={styles.errorIcon}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className={styles.errorTitle}>Connection Issue</h2>
          <p className={styles.errorBody}>
            We're having trouble loading your profile. This might be due to a slow connection.
          </p>
          <div className={styles.errorBtns}>
            <button onClick={() => retryFetchProfile()} className={styles.primaryBtn}>
              Try Again
            </button>
            <button onClick={() => window.location.reload()} className={styles.secondaryBtn}>
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If user is authenticated but has no profile or role, redirect to role selection
  if (user && (!profile || !profile.role)) {
    if (import.meta.env.VITE_DEBUG) {
      console.log('No profile/role found, redirecting to role selection');
    }
    return <Navigate to="/role-selection" replace />;
  }

  // No user and no loading means not authenticated
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (import.meta.env.VITE_DEBUG) {
    console.log('DashboardRouter - profile:', profile);
    console.log('DashboardRouter - role:', profile?.role);
  }

  if (profile?.role === 'business') {
    return <BusinessDashboard />;
  }

  return <CustomerDashboard />;
}

export default App;